import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Linking,
  Modal,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LiveVisitMap, LiveVisitMapPosition } from './LiveVisitMap';
import { useLiveDirections } from '../hooks/useLiveDirections';
import { colors, spacing, typography } from '../theme';
import { apiService, Visit, VisitStatus } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { getImageUrlSync } from '../utils/imageProxy';
import { withAlpha } from '../theme';

interface ContactVisitsCardProps {
  /** Omni-contact (parent) id — the same id visits store as contactId. */
  contactId: string;
  contactName: string;
  location?: { latitude?: number; longitude?: number; address?: string } | null;
  compact?: boolean;
  /** Bumped by the parent on profile mutations to force a reload. */
  refreshKey?: string | number;
  /** The visit currently open in the profile's visit workspace. */
  activeVisitId?: string | null;
  /** When given, start/end/open happen in the profile's visit workspace
   * (ContactVisitWorkspace) instead of navigating to the visit screens. */
  onOpenVisit?: (visitId: string) => void;
}

// History is loaded once (newest first); the card shows a short preview and
// expands inline rather than paging, matching the Orders/Quotations cards.
const LOAD_LIMIT = 20;
const PREVIEW_COUNT = 3;

const TERMINAL_STATUSES: VisitStatus[] = ['completed', 'missed', 'cancelled'];

const statusColor = (status: VisitStatus): string => {
  switch (status) {
    case 'completed': return colors.status.success as string;
    case 'missed':
    case 'cancelled': return colors.status.error as string;
    case 'in_progress': return colors.status.info as string;
    case 'rescheduled': return colors.status.warning as string;
    default: return colors.text.secondary as string;
  }
};

const statusIcon = (status: VisitStatus): keyof typeof Ionicons.glyphMap => {
  switch (status) {
    case 'completed': return 'checkmark-circle';
    case 'missed': return 'close-circle';
    case 'cancelled': return 'ban';
    case 'in_progress': return 'walk';
    case 'rescheduled': return 'calendar';
    case 'scheduled': return 'time';
    default: return 'ellipse-outline';
  }
};

const outcomeColor = (resultType?: 'success' | 'neutral' | 'failure'): string => {
  if (resultType === 'success') return colors.status.success as string;
  if (resultType === 'failure') return colors.status.error as string;
  return colors.status.info as string;
};

const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

/**
 * Visit history for one contact inside the contact profile. Lists the
 * contact's visits with status/outcome/photos and routes every stateful
 * action (start, end + outcome, required start/end photos) to the existing
 * VisitExecuteScreen rather than duplicating its location/photo enforcement
 * here. Directions open the in-app DISLIX map over this card, routed by the
 * server's maps engine (live-directions) exactly like VisitDetailScreen's
 * handleOpenLiveMap — never an external Maps app handoff. Claim and cancel
 * are simple API calls and happen inline.
 */
export const ContactVisitsCard: React.FC<ContactVisitsCardProps> = ({
  contactId,
  contactName,
  location,
  compact,
  refreshKey,
  activeVisitId,
  onOpenVisit,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const insets = useSafeAreaInsets();
  const { t } = useLocale();
  const { navigateTo, showNotification } = useAppContext();
  const { user } = useAppStore();
  const { hasPermissionAtLeast } = usePermissions();
  const canView = hasPermissionAtLeast('visits.view', 'view');
  const canExecute = hasPermissionAtLeast('visits.execute', 'edit');
  const canManage = hasPermissionAtLeast('visits.manageVisits', 'edit');

  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(false);
  // Hidden entirely when the visits service isn't available for this
  // business (list call fails) — no point showing an always-empty card.
  const [unavailable, setUnavailable] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Visit | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [liveMapVisit, setLiveMapVisit] = useState<Visit | null>(null);
  const liveDirections = useLiveDirections(liveMapVisit?._id, !!liveMapVisit);

  const loadVisits = useCallback(async () => {
    if (!contactId || !canView) return;
    setLoading(true);
    try {
      const res = await apiService.listVisits({ contactId, limit: LOAD_LIMIT, sortBy: 'createdAt', sortOrder: 'desc' });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setVisits(res.data);
        setUnavailable(false);
      } else {
        setVisits([]);
        setUnavailable(true);
      }
    } catch (error) {
      console.error('[ContactVisitsCard] Failed to load contact visits:', error);
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [contactId, canView]);

  useEffect(() => {
    setExpanded(false);
    loadVisits();
  }, [loadVisits, refreshKey]);

  // The agent's own visit already in progress with this contact opens in
  // the workspace straight away (once per contact).
  const autoOpenedFor = React.useRef<string | null>(null);
  useEffect(() => {
    if (!onOpenVisit || autoOpenedFor.current === contactId) return;
    const mine = visits.find((v) => v.status === 'in_progress' && (!v.assignedTo || v.assignedTo === user?.id));
    if (mine) {
      autoOpenedFor.current = contactId;
      onOpenVisit(mine._id);
    }
  }, [visits, contactId, onOpenVisit, user?.id]);

  if (!canView || unavailable) return null;

  const handleCreate = () => {
    navigateTo('visit-create', undefined, {
      prefillContact: {
        contactId,
        name: contactName,
        address: location?.address,
        latitude: location?.latitude,
        longitude: location?.longitude,
      },
    });
  };

  const openExecute = (visit: Visit) =>
    onOpenVisit ? onOpenVisit(visit._id) : navigateTo('visit-execute', undefined, { visitId: visit._id });
  // Open/active visits are worked in the profile; finished ones show their full record.
  const openDetail = (visit: Visit) =>
    onOpenVisit && !TERMINAL_STATUSES.includes(visit.status)
      ? onOpenVisit(visit._id)
      : navigateTo('visit-detail', undefined, { visitId: visit._id });

  // Agent's real GPS position -> server maps engine route to the visit's
  // target, shown in a fullscreen LiveVisitMap. The map opens immediately
  // (destination pin) and the route draws in once directions resolve.
  // Live position + road route to the visit's target while the map is
  // open (see useLiveDirections) — the marker and line follow the agent.
  const openDirections = (visit: Visit) => setLiveMapVisit(visit);
  const closeLiveMap = () => setLiveMapVisit(null);

  const handleClaim = async (visit: Visit) => {
    setActingId(visit._id);
    try {
      const res = await apiService.claimVisit(visit._id);
      if (res.statusCode !== 1000) throw new Error(res.errors?.[0]?.message || t('visits.errors.generic'));
      await loadVisits();
    } catch (error: any) {
      showNotification('error', t('contactEdit.section.visits'), error?.message || t('visits.errors.generic'));
    } finally {
      setActingId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    const target = cancelTarget;
    setActingId(target._id);
    try {
      const res = await apiService.cancelVisit(target._id, cancelReason.trim());
      if (res.statusCode !== 1000) throw new Error(res.errors?.[0]?.message || t('visits.errors.generic'));
      setCancelTarget(null);
      setCancelReason('');
      await loadVisits();
    } catch (error: any) {
      showNotification('error', t('contactEdit.section.visits'), error?.message || t('visits.errors.generic'));
    } finally {
      setActingId(null);
    }
  };

  const visibleVisits = expanded ? visits : visits.slice(0, PREVIEW_COUNT);

  const renderVisit = (visit: Visit, index: number) => {
    const color = statusColor(visit.status);
    const isTerminal = TERMINAL_STATUSES.includes(visit.status);
    const isMine = !!user?.id && visit.assignedTo === user.id;
    const canClaim = canExecute && !isTerminal && !!visit.departmentId && !visit.assignedTo;
    const canStart = canExecute && !canClaim && (visit.status === 'pending' || visit.status === 'scheduled' || visit.status === 'rescheduled')
      && (!visit.assignedTo || isMine);
    const canEnd = canExecute && visit.status === 'in_progress' && (!visit.assignedTo || isMine);
    const canCancel = canManage && !isTerminal;
    // Server routing needs the visit's own geocoded target.
    const hasTarget = typeof visit.targetLocation?.latitude === 'number' && typeof visit.targetLocation?.longitude === 'number';
    const photos = visit.photos || [];
    const isActing = actingId === visit._id;

    const when = visit.status === 'completed'
      ? formatDateTime(visit.endedAt || visit.startedAt)
      : visit.status === 'in_progress'
        ? formatDateTime(visit.startedAt)
        : formatDateTime(visit.scheduledFor || visit.createdAt);
    const subtitleParts = [
      when,
      typeof visit.actualDurationMinutes === 'number' ? `${Math.round(visit.actualDurationMinutes)} ${t('visits.detail.minutes')}` : '',
      visit.targetLocation?.address || '',
    ].filter(Boolean);

    return (
      <View key={visit._id} style={[styles.item, index === 0 && styles.itemFirst, activeVisitId === visit._id && styles.itemActive]}>
        <View style={[styles.itemTop, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.icon, { backgroundColor: `${color}18` }]}>
            <Ionicons name={statusIcon(visit.status)} size={15} color={color} />
          </View>
          <TouchableOpacity style={styles.info} onPress={() => openDetail(visit)} activeOpacity={0.75}>
            <Text style={[styles.rowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {t(`visits.status.${visit.status}` as any)}
            </Text>
            <Text style={[styles.rowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {subtitleParts.join(' · ')}
            </Text>
          </TouchableOpacity>
          {!!visit.outcome && (
            <View style={[styles.chip, { backgroundColor: `${outcomeColor(visit.outcome.resultType)}18` }]}>
              <Text style={[styles.chipText, { color: outcomeColor(visit.outcome.resultType) }]} numberOfLines={1}>
                {visit.outcome.label}
              </Text>
            </View>
          )}
        </View>

        {photos.length > 0 && (
          <View style={[styles.photosRow, { flexDirection: rtl.rowDirection }]}>
            {photos.slice(0, 6).map((photo, photoIndex) => {
              const uri = getImageUrlSync(photo.url, apiService.getBaseURL?.() ?? null);
              return (
                <TouchableOpacity
                  key={`${photo.mediaId || photo.url}-${photoIndex}`}
                  onPress={() => Linking.openURL(uri).catch(() => {})}
                  activeOpacity={0.8}
                >
                  <Image source={{ uri }} style={styles.photo} />
                  <View style={styles.photoPhase}>
                    <Text style={styles.photoPhaseText}>
                      {photo.phase === 'start' ? t('visits.execute.startPhotos') : t('visits.execute.endPhotos')}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
          {isActing ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <>
              {!isTerminal && hasTarget && canExecute && (
                <TouchableOpacity style={styles.actionButton} onPress={() => openDirections(visit)}>
                  <Ionicons name="navigate-outline" size={14} color={colors.primary} />
                  <Text style={styles.actionButtonText}>{t('visits.detail.getDirections')}</Text>
                </TouchableOpacity>
              )}
              {canClaim && (
                <TouchableOpacity style={styles.actionButton} onPress={() => handleClaim(visit)}>
                  <Ionicons name="person-add-outline" size={14} color={colors.primary} />
                  <Text style={styles.actionButtonText}>{t('visits.detail.claimVisit')}</Text>
                </TouchableOpacity>
              )}
              {canStart && (
                <TouchableOpacity style={[styles.actionButton, styles.actionButtonPrimary]} onPress={() => openExecute(visit)}>
                  <Ionicons name="play" size={13} color={colors.white} />
                  <Text style={[styles.actionButtonText, styles.actionButtonTextPrimary]}>{t('visits.detail.startVisit')}</Text>
                </TouchableOpacity>
              )}
              {canEnd && (
                <TouchableOpacity style={[styles.actionButton, styles.actionButtonPrimary]} onPress={() => openExecute(visit)}>
                  <Ionicons name="flag-outline" size={13} color={colors.white} />
                  <Text style={[styles.actionButtonText, styles.actionButtonTextPrimary]}>{t('contactEdit.visits.endVisit')}</Text>
                </TouchableOpacity>
              )}
              {canCancel && (
                <TouchableOpacity style={styles.iconButton} onPress={() => { setCancelReason(''); setCancelTarget(visit); }}>
                  <Ionicons name="close-circle-outline" size={15} color={colors.error} />
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.iconButton} onPress={() => openDetail(visit)}>
                <Ionicons name="open-outline" size={15} color={colors.text.secondary} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.card, compact && styles.cardInline]}>
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>
          {t('contactEdit.section.visits')}{visits.length > 0 ? ` (${visits.length}${visits.length >= LOAD_LIMIT ? '+' : ''})` : ''}
        </Text>
        {loading ? (
          <ActivityIndicator size="small" color={colors.primary} />
        ) : canManage && (
          <TouchableOpacity onPress={handleCreate} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
        )}
      </View>

      {visits.length > 0 ? (
        <>
          {visibleVisits.map(renderVisit)}
          {visits.length > PREVIEW_COUNT && (
            <TouchableOpacity style={styles.toggle} onPress={() => setExpanded((prev) => !prev)}>
              <Text style={styles.toggleText}>
                {expanded ? t('contactEdit.visits.showLess') : t('contactEdit.visits.showAll', { count: visits.length })}
              </Text>
              <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={13} color={colors.primary} />
            </TouchableOpacity>
          )}
        </>
      ) : (
        !loading && <Text style={styles.empty}>{t('contactEdit.visits.empty')}</Text>
      )}

      <Modal visible={!!liveMapVisit} animationType="fade" onRequestClose={closeLiveMap}>
        {!!liveMapVisit && typeof liveMapVisit.targetLocation?.latitude === 'number' && typeof liveMapVisit.targetLocation?.longitude === 'number' && (
          <View style={styles.mapFullscreenContainer}>
            <LiveVisitMap
              agentPosition={liveDirections.position as LiveVisitMapPosition | null}
              destination={{
                latitude: liveMapVisit.targetLocation.latitude,
                longitude: liveMapVisit.targetLocation.longitude,
                address: liveMapVisit.targetLocation.address,
              }}
              geometry={liveDirections.geometry}
              travelMode="driving"
              isFullscreen
            />
            <TouchableOpacity
              style={[styles.mapFullscreenCloseButton, { top: spacing.lg + insets.top }]}
              onPress={closeLiveMap}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="close" size={22} color={colors.white as string} />
            </TouchableOpacity>
          </View>
        )}
      </Modal>

      <Modal visible={!!cancelTarget} transparent animationType="fade" onRequestClose={() => setCancelTarget(null)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={[styles.modalTitle, { textAlign: rtl.textStart }]}>{t('visits.detail.cancelConfirmTitle')}</Text>
            <Text style={[styles.modalMessage, { textAlign: rtl.textStart }]}>{t('visits.detail.cancelConfirmMessage')}</Text>
            <Text style={[styles.modalLabel, { textAlign: rtl.textStart }]}>{t('visits.detail.cancelReasonLabel')}</Text>
            <TextInput
              style={[styles.modalInput, { textAlign: rtl.textStart }]}
              value={cancelReason}
              onChangeText={setCancelReason}
              placeholder={t('visits.detail.cancelReasonPlaceholder')}
              placeholderTextColor={colors.text.secondary as string}
              multiline
            />
            <View style={[styles.modalActions, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity style={styles.modalSecondary} onPress={() => setCancelTarget(null)}>
                <Text style={styles.modalSecondaryText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalDanger, (!cancelReason.trim() || !!actingId) && styles.modalDisabled]}
                onPress={handleConfirmCancel}
                disabled={!cancelReason.trim() || !!actingId}
              >
                {actingId ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.modalDangerText}>{t('visits.detail.cancelVisit')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Mirrors ContactEditForm's profileCard* styles so this card sits flush
// with the Orders/Quotations cards next to it.
const getStyles = () => StyleSheet.create({
  itemActive: {
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    paddingLeft: spacing.sm,
  },
  card: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    padding: spacing.md,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.04)' as any }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 2,
          elevation: 1,
        }),
  },
  cardInline: {
    flexGrow: 1,
    flexBasis: 280,
    minWidth: 260,
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  item: {
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  itemFirst: {
    borderTopWidth: 0,
  },
  itemTop: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  icon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  rowSubtitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: 2,
  },
  chip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 999,
    maxWidth: 140,
    flexShrink: 0,
  },
  chipText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.semibold,
  },
  photosRow: {
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  photo: {
    width: 56,
    height: 56,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.secondary,
  },
  photoPhase: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingVertical: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderBottomLeftRadius: spacing.borderRadius.sm,
    borderBottomRightRadius: spacing.borderRadius.sm,
  },
  photoPhaseText: {
    fontSize: 9,
    fontFamily: typography.fontFamily.medium,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  actions: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: withAlpha(colors.primary, 0.07),
  },
  actionButtonPrimary: {
    backgroundColor: colors.primary,
  },
  actionButtonText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  actionButtonTextPrimary: {
    color: colors.white,
  },
  iconButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  toggleText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  empty: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    paddingVertical: spacing.xs,
  },
  mapFullscreenContainer: {
    flex: 1,
    backgroundColor: '#000',
  },
  mapFullscreenCloseButton: {
    position: 'absolute',
    left: spacing.md,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    // Above LiveVisitMap's own fullscreen container (zIndex 1000).
    zIndex: 1001,
    elevation: 1001,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
  },
  modalCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  modalMessage: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  modalLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
    marginTop: spacing.xs,
  },
  modalInput: {
    minHeight: 72,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.sm,
    padding: spacing.sm,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    textAlignVertical: 'top',
  },
  modalActions: {
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  modalSecondary: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.secondary,
  },
  modalSecondaryText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  modalDanger: {
    minWidth: 110,
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.error,
  },
  modalDangerText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
  modalDisabled: {
    opacity: 0.5,
  },
});
